import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ACCREDITEDKYCWEBSERVICE } from "../CoreDataService";

const darkColors = {
  background: "#121212",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
};

const lightColors = {
  background: "#ffffff",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
};

/**
 * VestingSchedulePopup
 *
 * Fetches and displays the vesting schedule for a given currency.
 *
 * Props:
 *   open      - boolean
 *   onClose   - function
 *   theme     - "light" | "dark"
 *   currency  - string  e.g. "CO2COIN"
 */
const VestingSchedulePopup = ({ open, onClose, theme = "light", currency }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const colors = theme === "dark" ? darkColors : lightColors;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    if (!open || !currency) return;

    const uuid = localStorage.getItem("uuid") || "";
    const brokerId = localStorage.getItem("BROKERID") || "";

    const fetchSchedule = async () => {
      setLoading(true);
      setFetchError("");
      setRows([]);
      try {
        const response = await axios.get(
          `${ACCREDITEDKYCWEBSERVICE}accreditation/vesting-schedule`,
          {
            params: { uuid, brokerId, currency },
            headers: {
              Authorization: `BEARER ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (response.data?.success && Array.isArray(response.data?.data)) {
          setRows(response.data.data);
        } else {
          setFetchError(response.data?.message || "Failed to load vesting schedule.");
        }
      } catch {
        setFetchError("Unable to fetch vesting schedule. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [open, currency]);

  const headCellSx = {
    backgroundColor: theme === "dark" ? "#1a1a1a" : "#f0f0f0",
    color: colors.textSecondary,
    fontWeight: 700,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderColor: colors.border,
    whiteSpace: "nowrap",
    px: 2,
    py: 1.25,
  };

  const bodyCellSx = {
    color: colors.text,
    borderColor: colors.border,
    fontSize: 13,
    px: 2,
    py: 1.25,
    whiteSpace: "nowrap",
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={36} sx={{ color: colors.primary }} />
        </Box>
      );
    }

    if (fetchError) {
      return (
        <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
          <Typography sx={{ color: colors.textSecondary, fontSize: 14 }}>
            {fetchError}
          </Typography>
        </Box>
      );
    }

    if (rows.length === 0) {
      return (
        <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
          <Typography sx={{ color: colors.textSecondary, fontSize: 14 }}>
            No vesting schedule found for {currency}.
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Currency</TableCell>
              <TableCell sx={headCellSx} align="right">Percentage</TableCell>
              <TableCell sx={headCellSx} align="right">Duration (months)</TableCell>
              <TableCell sx={headCellSx} align="center">Status</TableCell>
              <TableCell sx={headCellSx}>Created On</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((item) => (
              <TableRow
                key={item.id}
                sx={{
                  "&:last-child td": { border: 0 },
                  "&:hover td": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                  },
                }}
              >
                <TableCell sx={bodyCellSx}>{currency}</TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  {item.percentage}%
                </TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  {item.duration}
                </TableCell>
                <TableCell sx={bodyCellSx} align="center">
                  <Chip
                    label={item.status === 1 ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      height: 22,
                      bgcolor:
                        item.status === 1
                          ? "rgba(34,197,94,0.15)"
                          : "rgba(156,163,175,0.2)",
                      color: item.status === 1 ? "#22c55e" : colors.textSecondary,
                      border: "none",
                    }}
                  />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  {item.createdOn ? item.createdOn.split(".")[0] : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow:
            theme === "dark"
              ? "0 8px 32px rgba(0,0,0,0.3)"
              : "0 8px 32px rgba(0,0,0,0.1)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pr: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text,
            fontSize: isMobile ? "1.1rem" : "1.35rem",
          }}
        >
          Vesting Schedule
          {currency && (
            <Box
              component="span"
              sx={{
                ml: 1,
                fontFamily: "monospace",
                fontSize: isMobile ? "0.85rem" : "1rem",
                bgcolor: "rgba(245,158,11,0.15)",
                color: "#f59e0b",
                px: "6px",
                py: "1px",
                borderRadius: 1,
              }}
            >
              {currency}
            </Box>
          )}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            position: "absolute",
            right: 16,
            "&:hover": {
              backgroundColor: theme === "dark" ? "#333" : "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>{renderContent()}</DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: `1px solid ${colors.border}`,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: 500,
            fontSize: 14,
            backgroundColor: colors.primary,
            "&:hover": {
              backgroundColor: theme === "dark" ? "#1565c0" : "#115293",
            },
            width: isMobile ? "100%" : "auto",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VestingSchedulePopup;
